// ── cohort-detail-helpers: id validation + status display + the tab registry ─
// Wave 3 (Cohorts). Shared by the cohort detail orchestrator + tabs. Ids are
// STRINGS (bigint-safe) — passed VERBATIM, never Number()'d. The status tone
// keys off the raw code; the label is humanized so a wider-than-write read set
// still renders (blueprint §1 trap 5).

import { humanizeLabel } from "@vjal/ui";

import type { CoachingRole } from "../../domain/roles";

// ── Cohort detail WORKSPACE access (participant isolation, FR-PDASH-001) ─────
// The /cohorts/:id admin workspace (roster, sessions, attendance, report status,
// …) is for STAFF / coach / company_admin / cohort_lead ONLY. The route manifest
// keeps `/cohorts/:id` at allRoles (preserved — do NOT alter it), but a
// `participant` (and any non-workspace role) must FAIL CLOSED at the page: their
// cohort context comes ONLY through the Wave-9 Participant Dashboard, never the
// admin workspace. This is an allowlist (fail-closed): an unknown/new role is
// denied until it is explicitly added here.
export const COHORT_WORKSPACE_ROLES: readonly CoachingRole[] = [
  "super_admin",
  "admin",
  "coach",
  "company_admin",
  "cohort_lead"
];

/** Whether the role may load the cohort admin workspace (fail-closed allowlist). */
export function canAccessCohortWorkspace(role: CoachingRole): boolean {
  return COHORT_WORKSPACE_ROLES.includes(role);
}

/** A non-empty, non-"new" route segment is the only thing that can be a real id. */
export function isValidCohortId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.trim().length > 0 && id !== "new";
}

export function cohortDisplayName(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  return trimmed.length > 0 ? trimmed : "Unnamed cohort";
}

export function cohortStatusLabel(status: string | null | undefined): string {
  return humanizeLabel(status ?? "active");
}

export function cohortStatusTone(status: string | null | undefined): "success" | "warning" | "default" | "danger" {
  switch (status) {
    case "active":
      return "success";
    case "completed":
      return "default";
    case "cancelled":
      return "danger";
    default:
      return "warning";
  }
}

/** Neutral display for a missing optional field — never a fabricated value. */
export function orDash(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : "—";
}

// ── The URL-driven tab registry ────────────────────────────────────────────
// Each tab is its OWN component (see cohort-detail/), wrapping its OWN
// useHonestQuery -> <HonestState> read. The tab id is carried in the URL as
// `?tab=<id>` so the detail is deep-linkable and browser back/forward works.
export const COHORT_DETAIL_TABS = [
  "overview",
  "participants",
  "sessions",
  "invites",
  "attendance",
  "progress",
  "reports",
  "recordings",
  "assignments"
] as const;

export type CohortDetailTab = (typeof COHORT_DETAIL_TABS)[number];

export const DEFAULT_COHORT_TAB: CohortDetailTab = "overview";

export const COHORT_TAB_LABELS: Record<CohortDetailTab, string> = {
  overview: "Overview",
  participants: "Participants",
  sessions: "Sessions",
  invites: "Invites",
  attendance: "Attendance",
  progress: "Progress",
  reports: "Reports",
  recordings: "Recordings",
  assignments: "Assignments"
};

/** Coerce an arbitrary URL value to a known tab id, falling back to the default. */
export function normalizeCohortTab(value: string | null | undefined): CohortDetailTab {
  return (COHORT_DETAIL_TABS as readonly string[]).includes(value ?? "")
    ? (value as CohortDetailTab)
    : DEFAULT_COHORT_TAB;
}
